// src/pages/CampaignCommercialDetailPage.jsx
// ORBIS PMO — C1.6.3 Acciones rápidas desde Ficha de Campaña
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";



async function safeJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; }
    catch { throw new Error(`Respuesta no JSON (${res.status}): ${text.slice(0, 220)}`); }
    if (!res.ok || data?.success === false) throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
    return data;
}

function n(value) { const x = Number(value || 0); return Number.isFinite(x) ? x : 0; }
function pct(value) { return `${Math.round(n(value))}%`; }
function money(value, currency = "MXN") { return `${currency || "MXN"} ${n(value).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`; }
function fmtDate(value) {
    if (!value || String(value).startsWith("0000-00-00")) return "—";
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("es-MX", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function defaultDateTimeLocal(minutesFromNow = 30) {
    const d = new Date();
    d.setMinutes(d.getMinutes() + minutesFromNow);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
}
function normalizeDateTimeForApi(v) {
    if (!v) return null;
    const x = String(v).replace("T", " ");
    return x.length === 16 ? `${x}:00` : x;
}

function Badge({ children, tone = "gray" }) {
    const tones = {
        gray: "bg-gray-50 text-gray-700 border-gray-200",
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        green: "bg-green-50 text-green-700 border-green-200",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        red: "bg-red-50 text-red-700 border-red-200",
        purple: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${tones[tone] || tones.gray}`}>{children}</span>;
}

function Kpi({ title, value, subtitle, tone = "default" }) {
    const tones = {
        default: "bg-white border-cani-border",
        blue: "bg-blue-50 border-blue-200",
        green: "bg-green-50 border-green-200",
        amber: "bg-amber-50 border-amber-200",
        red: "bg-red-50 border-red-200",
        purple: "bg-purple-50 border-purple-200",
    };
    return <div className={`rounded-2xl border shadow-sm p-4 ${tones[tone] || tones.default}`}><div className="text-[11px] text-gray-500">{title}</div><div className="text-2xl font-semibold text-cani-dark mt-1">{value}</div>{subtitle ? <div className="text-[10px] text-gray-500 mt-1">{subtitle}</div> : null}</div>;
}

function BarRow({ label, value, max, right, color = "#2563eb" }) {
    const width = max > 0 ? Math.max(4, Math.round((n(value) / max) * 100)) : 0;
    return <div className="space-y-1"><div className="flex justify-between gap-3 text-[11px]"><span className="font-medium text-gray-700 truncate">{label || "Sin dato"}</span><span className="text-gray-500 whitespace-nowrap">{right ?? value}</span></div><div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${width}%`, background: color }} /></div></div>;
}

function ProgressBar({ value, tone = "blue" }) {
    const colors = { red: "#dc2626", amber: "#d97706", blue: "#2563eb", green: "#059669", purple: "#7c3aed" };
    const width = Math.max(0, Math.min(100, n(value)));
    return <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${width}%`, background: colors[tone] || colors.blue }} /></div>;
}

function Section({ title, subtitle, right, children }) {
    return <div className="bg-white border border-cani-border rounded-2xl shadow-sm overflow-hidden"><div className="px-4 py-3 border-b border-cani-border flex items-start justify-between gap-3"><div><h3 className="text-xs font-semibold text-cani-dark">{title}</h3>{subtitle ? <p className="text-[10px] text-gray-500 mt-0.5">{subtitle}</p> : null}</div>{right ? <div>{right}</div> : null}</div>{children}</div>;
}

function Field({ label, children }) {
    return <label className="block"><span className="block text-[11px] text-gray-600 mb-1">{label}</span>{children}</label>;
}

function healthTone(level) {
    if (level === "saludable") return "green";
    if (level === "en_atencion") return "amber";
    return "red";
}

function Style() {
    return <style>{`
    .campaign-detail-pro table tbody tr { transition: background .18s ease; }
    .campaign-detail-pro table tbody tr:nth-child(odd) { background: #ffffff; }
    .campaign-detail-pro table tbody tr:nth-child(even) { background: #f8fafc; }
    .campaign-detail-pro table tbody tr:hover { background: #eff6ff !important; }
    .campaign-detail-pro .input-pro { width: 100%; border: 1px solid #e5e7eb; border-radius: .6rem; padding: .45rem .55rem; font-size: 12px; outline: none; background: white; }
    .campaign-detail-pro .input-pro:focus { border-color: #2563eb; box-shadow: 0 0 0 1px #2563eb; }
  `}</style>;
}

export default function CampaignCommercialDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState(null);
    const [summary, setSummary] = useState({});
    const [priorityActions, setPriorityActions] = useState([]);
    const [focus, setFocus] = useState({});
    const [byStage, setByStage] = useState([]);
    const [byProduct, setByProduct] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [activities, setActivities] = useState([]);
    const [focusMode, setFocusMode] = useState("without_next_action");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showValueModal, setShowValueModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);

    const [activityDraft, setActivityDraft] = useState({
        title: "",
        type: "seguimiento_propuesta",
        channel: "whatsapp",
        start_datetime: "",
        objective: "",
        next_action: "",
        next_action_date: "",
    });

    const [valueDraft, setValueDraft] = useState({
        pilot_value: "",
        monthly_potential: "",
    });

    const [bulkDraft, setBulkDraft] = useState({
        title: "Seguimiento comercial de campaña",
        type: "seguimiento_propuesta",
        channel: "whatsapp",
        start_datetime: "",
        objective: "Definir siguiente paso comercial de la oportunidad.",
    });

    async function load() {
        try {
            setLoading(true);
            setError("");
            const data = await safeJson(`${API_BASE}/campaign_commercial_detail.php?id=${id}`);
            setCampaign(data.campaign || null);
            setSummary(data.summary || {});
            setPriorityActions(Array.isArray(data.priority_actions) ? data.priority_actions : []);
            setFocus(data.focus || {});
            setByStage(Array.isArray(data.by_stage) ? data.by_stage : []);
            setByProduct(Array.isArray(data.by_product) ? data.by_product : []);
            setOpportunities(Array.isArray(data.opportunities) ? data.opportunities : []);
            setActivities(Array.isArray(data.activities) ? data.activities : []);
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo cargar el detalle de campaña.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [id]);

    const maxStage = useMemo(() => Math.max(0, ...byStage.map((r) => n(r.opportunities_count))), [byStage]);
    const maxProduct = useMemo(() => Math.max(0, ...byProduct.map((r) => n(r.opportunities_count))), [byProduct]);
    const focusRows = Array.isArray(focus?.[focusMode]) ? focus[focusMode] : [];
    const tone = healthTone(summary.health_level);

    function openActivityForOpportunity(opp) {
        setSelectedOpportunity(opp);
        setActivityDraft({
            title: `Seguimiento comercial - ${opp?.name || "Oportunidad"}`,
            type: "seguimiento_propuesta",
            channel: "whatsapp",
            start_datetime: defaultDateTimeLocal(60),
            objective: "Dar seguimiento comercial a la oportunidad y definir siguiente paso.",
            next_action: "",
            next_action_date: "",
        });
        setShowActivityModal(true);
    }

    function openValueForOpportunity(opp) {
        setSelectedOpportunity(opp);
        setValueDraft({
            pilot_value: opp?.pilot_value || "",
            monthly_potential: opp?.monthly_potential || "",
        });
        setShowValueModal(true);
    }

    function openBulkFollowup() {
        const rows = focusRows.length ? focusRows : [];
        setBulkDraft({
            title: "Seguimiento comercial de campaña",
            type: "seguimiento_propuesta",
            channel: "whatsapp",
            start_datetime: defaultDateTimeLocal(60),
            objective: "Definir siguiente paso comercial de la oportunidad.",
        });
        if (rows.length === 0) {
            setError("No hay oportunidades visibles para crear seguimiento.");
            return;
        }
        setShowBulkModal(true);
    }

    async function createActivity(opp, draft) {
        return safeJson(`${API_BASE}/activity_create.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                account_id: opp?.account_id ? Number(opp.account_id) : null,
                opportunity_id: opp?.id ? Number(opp.id) : null,
                type: draft.type,
                channel: draft.channel,
                title: draft.title,
                objective: draft.objective,
                status: "programada",
                start_datetime: normalizeDateTimeForApi(draft.start_datetime),
                next_action: draft.next_action || null,
                next_action_date: normalizeDateTimeForApi(draft.next_action_date),
                alerts: [],
            }),
        });
    }

    async function saveQuickActivity(e) {
        e?.preventDefault?.();
        if (!selectedOpportunity?.id) return setError("Selecciona una oportunidad.");
        if (!activityDraft.title) return setError("El título de la actividad es obligatorio.");
        if (!activityDraft.start_datetime) return setError("La fecha de actividad es obligatoria.");

        try {
            setSaving(true);
            setError("");
            setToast("");
            await createActivity(selectedOpportunity, activityDraft);
            setShowActivityModal(false);
            setToast("Actividad creada desde la campaña.");
            await load();
        } catch (err) {
            setError(err.message || "No se pudo crear la actividad.");
        } finally {
            setSaving(false);
        }
    }

    async function saveQuickValue(e) {
        e?.preventDefault?.();
        if (!selectedOpportunity?.id) return setError("Selecciona una oportunidad.");

        try {
            setSaving(true);
            setError("");
            setToast("");
            await safeJson(`${API_BASE}/opportunity_commercial_quick_update.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    opportunity_id: Number(selectedOpportunity.id),
                    pilot_value: valueDraft.pilot_value !== "" ? Number(valueDraft.pilot_value) : null,
                    monthly_potential: valueDraft.monthly_potential !== "" ? Number(valueDraft.monthly_potential) : null,
                }),
            });
            setShowValueModal(false);
            setToast("Valor comercial actualizado desde la campaña.");
            await load();
        } catch (err) {
            setError(err.message || "No se pudo actualizar el valor comercial.");
        } finally {
            setSaving(false);
        }
    }

    async function createBulkFollowup(e) {
        e?.preventDefault?.();
        const rows = focusRows.length ? focusRows : [];
        if (rows.length === 0) return setError("No hay oportunidades visibles para seguimiento.");
        if (!bulkDraft.title) return setError("El título base es obligatorio.");
        if (!bulkDraft.start_datetime) return setError("La fecha base es obligatoria.");

        try {
            setSaving(true);
            setError("");
            setToast("");

            let created = 0;
            for (let i = 0; i < rows.length; i += 1) {
                const opp = rows[i];
                const d = new Date(String(bulkDraft.start_datetime).replace("T", " "));
                d.setMinutes(d.getMinutes() + i * 15);
                d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                const local = d.toISOString().slice(0, 16);

                await createActivity(opp, {
                    ...bulkDraft,
                    title: `${bulkDraft.title} - ${opp.name}`,
                    start_datetime: local,
                    next_action: "",
                    next_action_date: "",
                });
                created += 1;
            }

            setShowBulkModal(false);
            setToast(`Se crearon ${created} seguimiento(s) desde la campaña.`);
            await load();
        } catch (err) {
            setError(err.message || "No se pudieron crear los seguimientos.");
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <MainLayout><div className="p-4 text-sm text-gray-500">Cargando campaña comercial...</div></MainLayout>;

    function ActionButtons({ opp }) {
        return (
            <div className="flex flex-wrap justify-end gap-1">
                <button type="button" onClick={() => navigate(`/opportunities/${opp.id}/edit`)} className="px-2 py-1 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg text-[11px]">Abrir ficha</button>
                <button type="button" onClick={() => openActivityForOpportunity(opp)} className="px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[11px]">Actividad</button>
                <button type="button" onClick={() => openValueForOpportunity(opp)} className="px-2 py-1 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 text-[11px]">Valor</button>
            </div>
        );
    }

    return (
        <MainLayout>
            <Style />
            <div className="p-4 space-y-4 campaign-detail-pro">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                    <div>
                        <button type="button" onClick={() => navigate("/campaigns-commercial")} className="text-[11px] text-cani-primary hover:underline mb-2">← Regresar a campañas</button>
                        <h2 className="text-lg md:text-xl font-semibold text-cani-dark">Ficha de Campaña Comercial</h2>
                        <p className="text-xs md:text-sm text-gray-500 max-w-3xl">{campaign?.name || "Campaña"} · {campaign?.segment || "Sin segmento definido"}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <button type="button" onClick={() => navigate(`/opportunities?campaign_id=${id}`)} className="px-3 py-1.5 rounded-lg border border-cani-border text-cani-dark hover:bg-cani-bg">Oportunidades</button>
                        <button type="button" onClick={() => navigate(`/agenda?campaign_id=${id}`)} className="px-3 py-1.5 rounded-lg border border-cani-border text-cani-dark hover:bg-cani-bg">Agenda</button>
                        <button type="button" onClick={() => navigate(`/opportunities/new?campaign_id=${id}`)} className="px-3 py-1.5 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100">+ Oportunidad</button>
                        {campaign?.landing_url ? <a href={campaign.landing_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight">Landing</a> : null}
                        <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight">Recargar</button>
                    </div>
                </div>

                {toast ? <div className="text-xs rounded-lg px-3 py-2 border bg-green-50 text-green-700 border-green-200">{toast}</div> : null}
                {error ? <div className="text-xs rounded-lg px-3 py-2 border bg-red-50 text-red-700 border-red-200">{error}</div> : null}

                <div className={`rounded-2xl border shadow-sm p-4 ${tone === "green" ? "bg-green-50 border-green-200" : tone === "amber" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        <div>
                            <div className="text-[11px] text-gray-500">Salud de campaña</div>
                            <div className="text-3xl font-semibold text-cani-dark mt-1">{summary.health_score || 0}/100</div>
                            <div className="mt-2"><ProgressBar value={summary.health_score || 0} tone={tone} /></div>
                            <div className="mt-2"><Badge tone={tone}>{summary.health_level || "sin lectura"}</Badge></div>
                        </div>
                        <div className="lg:col-span-2">
                            <div className="text-[11px] text-gray-500">Lectura ejecutiva</div>
                            <div className="text-sm font-semibold text-cani-dark mt-1">
                                {summary.health_level === "saludable" ? "La campaña tiene una operación comercial saludable." : summary.health_level === "en_atencion" ? "La campaña genera oportunidades, pero requiere mejorar seguimiento y captura." : "La campaña requiere intervención comercial inmediata."}
                            </div>
                            <p className="text-xs text-gray-600 mt-1">El score combina próxima acción, contacto clave, actividad vinculada, valor piloto, potencial mensual y penalización por actividades vencidas.</p>
                        </div>
                        <div>
                            <div className="text-[11px] text-gray-500">Acción sugerida</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {priorityActions.length === 0 ? <Badge tone="green">Sin acciones críticas</Badge> : priorityActions.slice(0, 3).map((a) => <Badge key={a.type} tone={a.tone}>{a.label}: {a.count}</Badge>)}
                            </div>
                        </div>
                    </div>
                </div>

                <Section title="Información base de campaña" subtitle="Objetivo, canal, segmento, landing y presupuesto.">
                    <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
                        <div><div className="text-[11px] text-gray-500">Objetivo</div><div className="font-medium text-cani-dark mt-1">{campaign?.objective || "Sin objetivo registrado."}</div></div>
                        <div><div className="text-[11px] text-gray-500">Canal</div><div className="font-medium text-cani-dark mt-1">{campaign?.channel || "Sin canal registrado."}</div></div>
                        <div><div className="text-[11px] text-gray-500">Estatus / presupuesto</div><div className="font-medium text-cani-dark mt-1"><Badge tone={campaign?.status === "activa" ? "green" : "amber"}>{campaign?.status || "sin estatus"}</Badge> <span className="ml-2">{money(campaign?.budget, campaign?.currency)}</span></div>{campaign?.landing_url ? <a className="text-[11px] text-cani-primary hover:underline mt-1 inline-block" href={campaign.landing_url} target="_blank" rel="noreferrer">Abrir landing</a> : <div className="text-[11px] text-gray-400 mt-1">Sin landing vinculada</div>}</div>
                    </div>
                </Section>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    <Kpi title="Oportunidades" value={summary.opportunities_count || 0} subtitle={`${summary.open_count || 0} abiertas`} tone="blue" />
                    <Kpi title="Conversión" value={pct(summary.conversion_rate)} subtitle={`${summary.won_count || 0} ganadas`} tone="green" />
                    <Kpi title="Próxima acción" value={pct(summary.next_action_rate)} subtitle={`${summary.with_next_action || 0}/${summary.opportunities_count || 0}`} tone={n(summary.next_action_rate) >= 70 ? "green" : "red"} />
                    <Kpi title="Valor piloto" value={money(summary.pilot_value, campaign?.currency)} subtitle="Pipeline generado" tone="purple" />
                    <Kpi title="Potencial mensual" value={money(summary.monthly_potential, campaign?.currency)} subtitle="Continuidad estimada" tone="green" />
                    <Kpi title="Con contacto" value={pct(summary.contact_rate)} subtitle={`${summary.with_contact || 0}/${summary.opportunities_count || 0}`} tone={n(summary.contact_rate) >= 70 ? "green" : "amber"} />
                    <Kpi title="Con actividad" value={pct(summary.activity_rate)} subtitle={`${summary.with_activity || 0}/${summary.opportunities_count || 0}`} tone={n(summary.activity_rate) >= 70 ? "green" : "amber"} />
                    <Kpi title="Actividades vencidas" value={summary.overdue_activities || 0} subtitle="Atención requerida" tone={n(summary.overdue_activities) > 0 ? "red" : "green"} />
                    <Kpi title="Sin contacto" value={summary.without_contact || 0} subtitle="Falta responsable comercial" tone={n(summary.without_contact) > 0 ? "amber" : "green"} />
                    <Kpi title="Sin valor" value={summary.without_pilot_value || 0} subtitle="Falta valor piloto" tone={n(summary.without_pilot_value) > 0 ? "amber" : "green"} />
                </div>

                <Section
                    title="Qué atender en esta campaña"
                    subtitle="Oportunidades que frenan la calidad comercial de la campaña."
                    right={<button type="button" onClick={openBulkFollowup} className="px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[11px]">Seguimiento visibles</button>}
                >
                    <div className="p-4 space-y-3">
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            <button type="button" onClick={() => setFocusMode("without_next_action")} className={`px-3 py-1.5 rounded-lg border ${focusMode === "without_next_action" ? "bg-red-50 border-red-200 text-red-700" : "border-cani-border text-gray-700"}`}>Sin próxima acción</button>
                            <button type="button" onClick={() => setFocusMode("without_contact")} className={`px-3 py-1.5 rounded-lg border ${focusMode === "without_contact" ? "bg-amber-50 border-amber-200 text-amber-700" : "border-cani-border text-gray-700"}`}>Sin contacto</button>
                            <button type="button" onClick={() => setFocusMode("without_value")} className={`px-3 py-1.5 rounded-lg border ${focusMode === "without_value" ? "bg-amber-50 border-amber-200 text-amber-700" : "border-cani-border text-gray-700"}`}>Sin valor</button>
                            <button type="button" onClick={() => setFocusMode("with_overdue_activity")} className={`px-3 py-1.5 rounded-lg border ${focusMode === "with_overdue_activity" ? "bg-red-50 border-red-200 text-red-700" : "border-cani-border text-gray-700"}`}>Con actividad vencida</button>
                        </div>

                        {focusRows.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-cani-border p-4 text-xs text-gray-500 bg-cani-bg/30">No hay oportunidades en este criterio.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-[1120px] w-full text-xs">
                                    <thead className="bg-cani-bg"><tr className="text-left text-[11px] text-gray-600 font-semibold"><th className="px-3 py-2">Oportunidad</th><th className="px-3 py-2">Cuenta</th><th className="px-3 py-2">Etapa</th><th className="px-3 py-2">Valor</th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                                    <tbody>{focusRows.map((o) => <tr key={o.id} className="border-t border-cani-border"><td className="px-3 py-2"><div className="font-semibold text-cani-dark">{o.name}</div><div className="text-[11px] text-gray-500">{o.product_line || "Sin producto"}</div></td><td className="px-3 py-2">{o.account_name || "Sin cuenta"}</td><td className="px-3 py-2"><Badge tone="blue">{o.stage_name || "Sin etapa"}</Badge></td><td className="px-3 py-2">{money(o.pilot_value, campaign?.currency)}<div className="text-[11px] text-gray-500">Mensual {money(o.monthly_potential, campaign?.currency)}</div></td><td className="px-3 py-2 text-right"><ActionButtons opp={o} /></td></tr>)}</tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Section>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <Section title="Pipeline por etapa" subtitle="Distribución de oportunidades por etapa."><div className="p-4 space-y-3">{byStage.length === 0 ? <div className="text-xs text-gray-400">Sin etapas.</div> : byStage.map((r) => <BarRow key={r.stage_id || r.stage_name} label={r.stage_name || "Sin etapa"} value={r.opportunities_count} max={maxStage} right={`${r.opportunities_count || 0} · ${money(r.pilot_value, campaign?.currency)}`} color={r.color_hex || "#2563eb"} />)}</div></Section>
                    <Section title="Producto / línea" subtitle="Distribución comercial por producto."><div className="p-4 space-y-3">{byProduct.length === 0 ? <div className="text-xs text-gray-400">Sin productos.</div> : byProduct.map((r) => <BarRow key={r.product_line} label={r.product_line || "Sin producto"} value={r.opportunities_count} max={maxProduct} right={`${r.opportunities_count || 0} · ${money(r.pilot_value, campaign?.currency)}`} color="#0f766e" />)}</div></Section>
                </div>

                <Section title="Oportunidades de la campaña" subtitle="Pipeline completo generado por esta campaña.">
                    <div className="overflow-x-auto">
                        <table className="min-w-[1280px] w-full text-xs">
                            <thead className="bg-cani-bg"><tr className="text-left text-[11px] text-gray-600 font-semibold"><th className="px-3 py-2">Oportunidad</th><th className="px-3 py-2">Cuenta</th><th className="px-3 py-2">Etapa</th><th className="px-3 py-2">Calidad</th><th className="px-3 py-2">Valor</th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                            <tbody>{opportunities.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Sin oportunidades en esta campaña.</td></tr> : opportunities.map((o) => <tr key={o.id} className="border-t border-cani-border"><td className="px-3 py-2"><button type="button" onClick={() => navigate(`/opportunities/${o.id}/edit`)} className="font-semibold text-cani-dark hover:text-cani-primary">{o.name}</button><div className="text-[11px] text-gray-500">{o.product_line || "Sin producto"}</div></td><td className="px-3 py-2">{o.account_name || "Sin cuenta"}</td><td className="px-3 py-2"><Badge tone={o.stage_type === "ganada" ? "green" : o.stage_type === "perdida" ? "red" : "blue"}>{o.stage_name || "Sin etapa"}</Badge></td><td className="px-3 py-2"><div className="flex flex-wrap gap-1"><Badge tone={Number(o.has_contact) ? "green" : "red"}>Contacto</Badge><Badge tone={Number(o.has_activity) ? "green" : "red"}>Actividad</Badge>{o.next_action ? <Badge tone="green">Próx.</Badge> : <Badge tone="red">Próx.</Badge>}{Number(o.has_overdue_activity) ? <Badge tone="red">Vencida</Badge> : null}</div></td><td className="px-3 py-2">{money(o.pilot_value, campaign?.currency)}<div className="text-[11px] text-gray-500">Mensual {money(o.monthly_potential, campaign?.currency)}</div></td><td className="px-3 py-2 text-right"><ActionButtons opp={o} /></td></tr>)}</tbody>
                        </table>
                    </div>
                </Section>

                <Section title="Actividades recientes de la campaña" subtitle="Seguimientos, reuniones, demos y actividades vencidas.">
                    <div className="overflow-x-auto">
                        <table className="min-w-[1000px] w-full text-xs">
                            <thead className="bg-cani-bg"><tr className="text-left text-[11px] text-gray-600 font-semibold"><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Actividad</th><th className="px-3 py-2">Oportunidad</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2 text-right">Acción</th></tr></thead>
                            <tbody>{activities.length === 0 ? <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Sin actividades en esta campaña.</td></tr> : activities.map((a) => <tr key={a.id} className="border-t border-cani-border"><td className="px-3 py-2">{fmtDate(a.start_datetime)}</td><td className="px-3 py-2"><div className="font-semibold text-cani-dark">{a.title}</div><div className="text-[11px] text-gray-500">{a.type || "Sin tipo"} · {a.channel || "Sin canal"}</div></td><td className="px-3 py-2">{a.opportunity_name || "Sin oportunidad"}</td><td className="px-3 py-2"><Badge tone={Number(a.is_overdue) ? "red" : a.status === "realizada" ? "green" : "blue"}>{Number(a.is_overdue) ? "vencida" : a.status || "programada"}</Badge></td><td className="px-3 py-2 text-right"><button type="button" onClick={() => navigate(a.opportunity_id ? `/opportunities/${a.opportunity_id}/edit` : "/agenda")} className="px-2 py-1 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg text-[11px]">Abrir</button></td></tr>)}</tbody>
                        </table>
                    </div>
                </Section>

                {showActivityModal ? (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl border border-cani-border w-full max-w-3xl">
                            <div className="px-5 py-4 border-b border-cani-border flex items-start justify-between gap-3">
                                <div><h3 className="text-sm font-semibold text-cani-dark">Crear actividad desde campaña</h3><p className="text-[11px] text-gray-500">{selectedOpportunity?.name || "Oportunidad"}</p></div>
                                <button type="button" onClick={() => setShowActivityModal(false)} className="text-xs text-gray-500 hover:text-gray-700">Cerrar</button>
                            </div>
                            <form onSubmit={saveQuickActivity} className="p-5 space-y-3 text-xs">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div className="md:col-span-2"><Field label="Título *"><input value={activityDraft.title} onChange={(e) => setActivityDraft((p) => ({ ...p, title: e.target.value }))} className="input-pro" /></Field></div>
                                    <Field label="Fecha *"><input type="datetime-local" value={activityDraft.start_datetime} onChange={(e) => setActivityDraft((p) => ({ ...p, start_datetime: e.target.value }))} className="input-pro" /></Field>
                                    <Field label="Tipo"><select value={activityDraft.type} onChange={(e) => setActivityDraft((p) => ({ ...p, type: e.target.value }))} className="input-pro"><option value="seguimiento_propuesta">Seguimiento propuesta</option><option value="demo_orbis">Demo ORBIS</option><option value="reunion_virtual">Reunión virtual</option><option value="reunion_presencial">Reunión presencial</option><option value="llamada">Llamada</option><option value="whatsapp">WhatsApp</option><option value="correo">Correo</option></select></Field>
                                    <Field label="Canal"><select value={activityDraft.channel} onChange={(e) => setActivityDraft((p) => ({ ...p, channel: e.target.value }))} className="input-pro"><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="telefono">Teléfono</option><option value="meet">Meet</option><option value="presencial">Presencial</option><option value="linkedin">LinkedIn</option></select></Field>
                                    <div className="md:col-span-3"><Field label="Objetivo"><textarea rows={3} value={activityDraft.objective} onChange={(e) => setActivityDraft((p) => ({ ...p, objective: e.target.value }))} className="input-pro resize-none" /></Field></div>
                                    <div className="md:col-span-2"><Field label="Siguiente acción opcional"><input value={activityDraft.next_action} onChange={(e) => setActivityDraft((p) => ({ ...p, next_action: e.target.value }))} className="input-pro" /></Field></div>
                                    <Field label="Fecha siguiente acción"><input type="datetime-local" value={activityDraft.next_action_date} onChange={(e) => setActivityDraft((p) => ({ ...p, next_action_date: e.target.value }))} className="input-pro" /></Field>
                                </div>
                                <div className="flex justify-end gap-2 pt-2 border-t border-cani-border"><button type="button" onClick={() => setShowActivityModal(false)} className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg">Cancelar</button><button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight disabled:opacity-60">{saving ? "Guardando..." : "Crear actividad"}</button></div>
                            </form>
                        </div>
                    </div>
                ) : null}

                {showValueModal ? (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl border border-cani-border w-full max-w-xl">
                            <div className="px-5 py-4 border-b border-cani-border flex items-start justify-between gap-3">
                                <div><h3 className="text-sm font-semibold text-cani-dark">Capturar valor desde campaña</h3><p className="text-[11px] text-gray-500">{selectedOpportunity?.name || "Oportunidad"}</p></div>
                                <button type="button" onClick={() => setShowValueModal(false)} className="text-xs text-gray-500 hover:text-gray-700">Cerrar</button>
                            </div>
                            <form onSubmit={saveQuickValue} className="p-5 space-y-3 text-xs">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field label="Valor piloto"><input type="number" min="0" value={valueDraft.pilot_value} onChange={(e) => setValueDraft((p) => ({ ...p, pilot_value: e.target.value }))} className="input-pro" /></Field>
                                    <Field label="Potencial mensual"><input type="number" min="0" value={valueDraft.monthly_potential} onChange={(e) => setValueDraft((p) => ({ ...p, monthly_potential: e.target.value }))} className="input-pro" /></Field>
                                </div>
                                <div className="flex justify-end gap-2 pt-2 border-t border-cani-border"><button type="button" onClick={() => setShowValueModal(false)} className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg">Cancelar</button><button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight disabled:opacity-60">{saving ? "Guardando..." : "Guardar valor"}</button></div>
                            </form>
                        </div>
                    </div>
                ) : null}

                {showBulkModal ? (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl border border-cani-border w-full max-w-2xl">
                            <div className="px-5 py-4 border-b border-cani-border flex items-start justify-between gap-3">
                                <div><h3 className="text-sm font-semibold text-cani-dark">Crear seguimiento para oportunidades visibles</h3><p className="text-[11px] text-gray-500">Se creará una actividad para {focusRows.length} oportunidad(es) del criterio activo.</p></div>
                                <button type="button" onClick={() => setShowBulkModal(false)} className="text-xs text-gray-500 hover:text-gray-700">Cerrar</button>
                            </div>
                            <form onSubmit={createBulkFollowup} className="p-5 space-y-3 text-xs">
                                <Field label="Título base *"><input value={bulkDraft.title} onChange={(e) => setBulkDraft((p) => ({ ...p, title: e.target.value }))} className="input-pro" /></Field>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <Field label="Fecha base *"><input type="datetime-local" value={bulkDraft.start_datetime} onChange={(e) => setBulkDraft((p) => ({ ...p, start_datetime: e.target.value }))} className="input-pro" /></Field>
                                    <Field label="Tipo"><select value={bulkDraft.type} onChange={(e) => setBulkDraft((p) => ({ ...p, type: e.target.value }))} className="input-pro"><option value="seguimiento_propuesta">Seguimiento propuesta</option><option value="llamada">Llamada</option><option value="whatsapp">WhatsApp</option><option value="correo">Correo</option><option value="reunion_virtual">Reunión virtual</option></select></Field>
                                    <Field label="Canal"><select value={bulkDraft.channel} onChange={(e) => setBulkDraft((p) => ({ ...p, channel: e.target.value }))} className="input-pro"><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="telefono">Teléfono</option><option value="meet">Meet</option><option value="presencial">Presencial</option><option value="linkedin">LinkedIn</option></select></Field>
                                </div>
                                <Field label="Objetivo"><textarea rows={3} value={bulkDraft.objective} onChange={(e) => setBulkDraft((p) => ({ ...p, objective: e.target.value }))} className="input-pro resize-none" /></Field>
                                <div className="flex justify-end gap-2 pt-2 border-t border-cani-border"><button type="button" onClick={() => setShowBulkModal(false)} className="px-3 py-1.5 rounded-lg border border-cani-border text-gray-700 hover:bg-cani-bg">Cancelar</button><button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg bg-cani-primary text-white hover:bg-cani-primaryLight disabled:opacity-60">{saving ? "Creando..." : `Crear ${focusRows.length} seguimiento(s)`}</button></div>
                            </form>
                        </div>
                    </div>
                ) : null}
            </div>
        </MainLayout>
    );
}
